<!-- ─────────────────────────────────────────────────────────────────────── -->
<!-- Conversation Detail                                                   -->
<!-- Right-hand panel: empty state, timeline, and bulk actions              -->
<!-- ─────────────────────────────────────────────────────────────────────── -->
<div class="conversation-detail">

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- Empty State – No conversation selected                            -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <div class="conversation-detail__empty" *ngIf="!activeConversation && !activeConversationDetails && !isDeepLinkLoading">
        <ui-svg-icon svgIcon="inbox" size="xlarge"></ui-svg-icon>
        <h3>{{ 'smart_inbox.inbox_view.select_email.title' | translate }}</h3>
        <p>{{ 'smart_inbox.inbox_view.select_email.description' | translate }}</p>
        <div>
            <span>{{ 'smart_inbox.inbox_view.select_email.actions.categorize_emails' | translate }}</span>
            <span>{{ 'smart_inbox.inbox_view.select_email.actions.assign_to_team_members' | translate }}</span>
            <span>{{ 'smart_inbox.inbox_view.select_email.actions.generate_ai_support_responses' | translate }}</span>
            <span>{{ 'smart_inbox.inbox_view.select_email.actions.create_orders_one_click' | translate }}</span>
        </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- Loading State – Thread loading from deep link                     -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <div class="conversation-detail__empty pulse-bg" *ngIf="isDeepLinkLoading">
        <ui-svg-icon svgIcon="refresh-cc" size="xlarge" class="spin-animation"></ui-svg-icon>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- Conversation Content (when a conversation is selected)            -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <div class="conversation-detail__container" *ngIf="(activeConversation && !isDeepLinkLoading) || (activeConversationDetails && !conversationDetailsLoading)">
        <!-- ─── Header ────────────────────────────────────────────────── -->
        <div class="conversation-detail__header">
            <!-- Row 1: subject title + status pill + more menu -->
            <div class="conversation-detail__header-top">
                <div class="conversation-detail__subject" [innerHTML]="(activeConversation?.subject || activeConversationDetails?.subject) | highlight: searchTerm" [matTooltip]="activeConversation?.subject || activeConversationDetails?.subject || ''" matTooltipClass="conversation-tooltip-info" matTooltipPosition="below"></div>
                <div class="conversation-detail__header-top-actions">
                    <!-- Urgency toggle. The operator's choice is sticky: later
                         AI-urgent messages will not flip the thread back. -->
                    <button type="button"
                            class="conversation-detail__urgency"
                            [class.is-urgent]="isThreadUrgent"
                            [disabled]="updatingUrgency"
                            (click)="onToggleUrgency()"
                            [attr.aria-pressed]="isThreadUrgent"
                            [matTooltip]="(isThreadUrgent ? 'smart_inbox.inbox_view.detail.urgency_unmark' : 'smart_inbox.inbox_view.detail.urgency_mark') | translate"
                            [matTooltipClass]="isThreadUrgent ? 'conversation-tooltip-warning' : 'conversation-tooltip-info'"
                            matTooltipPosition="below">
                        <ui-svg-icon *ngIf="!updatingUrgency" [svgIcon]="isThreadUrgent ? 'flag-filled' : 'flag'" size="medium"></ui-svg-icon>
                        <ui-svg-icon *ngIf="updatingUrgency" svgIcon="refresh-cc" size="medium" class="spin-animation"></ui-svg-icon>
                    </button>

                    <!-- Status pill -->
                    <span class="conversation-tag is-badge is-action"
                            [class]="'status-' + (activeConversation?.status || activeConversationDetails?.status || 'new')"
                            (click)="setActiveConversationMenu(CONVERSATION_MENU_OPTIONS.STATUS, 'single')"
                            [matMenuTriggerFor]="conversationActionMenuRef.menu">
                        <ng-container *ngIf="updatingStatus" [ngTemplateOutlet]="badgeLoader"></ng-container>
                        <ng-container *ngIf="!updatingStatus">
                            <ui-svg-icon [svgIcon]="activeConversation?.status || activeConversationDetails?.status || 'new'" size="medium"></ui-svg-icon>
                            {{ (activeConversation?.status || activeConversationDetails?.status ? 'smart_inbox.inbox_view.detail.status_' + (activeConversation?.status || activeConversationDetails?.status) : 'smart_inbox.inbox_view.detail.status_new') | translate }}
                            <ui-svg-icon svgIcon="chevron-down" size="medium"></ui-svg-icon>
                        </ng-container>
                    </span>
                    <!-- More menu (create order, mark unread, …) -->
                    <ui-button type="transparent" icon="more_vert" iconSize="medium" [matMenuTriggerFor]="conversationActionMenuRef.menu" (click)="setActiveConversationMenu(CONVERSATION_MENU_OPTIONS.MORE, 'single')">
                    </ui-button>
                </div>
            </div>

            <!-- Row 2: company + sender email -->
            <div class="conversation-detail__header-identity">
                <span class="conversation-detail__company"
                      [class.is-unassociated]="!hasAssociatedCompany()"
                      [matMenuTriggerFor]="customerAssociationMenu" #customerMenuTrigger="matMenuTrigger"
                      (click)="conversationUpdateType = 'single'; onOpenCustomerMenu()"
                      [matTooltip]="hasAssociatedCompany() ? getConversationTitle() : ('smart_inbox.inbox_view.detail.no_company_association' | translate)"
                      [matTooltipClass]="hasAssociatedCompany() ? 'conversation-tooltip-info' : 'conversation-tooltip-warning'"
                      matTooltipPosition="below">
                    <ui-svg-icon [svgIcon]="hasAssociatedCompany() ? 'companies' : 'email'" size="medium"></ui-svg-icon>
                    <span class="conversation-detail__company-name">{{ getConversationTitle() }}</span>
                </span>
                <span class="conversation-detail__identity-sep" *ngIf="getConversationSender()">·</span>
                <span class="conversation-detail__sender" [matTooltip]="getConversationSender()" matTooltipClass="conversation-tooltip-info" matTooltipPosition="below">{{ getConversationSender() }}</span>
            </div>
        </div>

        <!-- ─── Action items (category, assignee, view preferences) ──────── -->
        <div class="conversation-detail__badges" [attr.aria-label]="'smart_inbox.inbox_view.detail.conversation_classifications_and_status' | translate">

            <div class="conversation-detail__badges-left">
                <!-- Which mailbox received the thread. Sits outside the categories
                     block so it still shows on an unclassified thread. -->
                <ui-initials-tag
                    *ngIf="mailboxTag as tag"
                    size="medium"
                    [label]="tag.label"
                    [ariaLabel]="tag.ariaLabel"
                    [color]="tag.bg"
                    [borderColor]="tag.border"
                    [textColor]="tag.text"
                    [matTooltip]="tag.tooltip"
                    matTooltipClass="inbox-account-tooltip"
                    matTooltipPosition="below">
                </ui-initials-tag>

                <!-- Counterparty: read-only here, corrected per message below. -->
                <ui-ai-classification-tag
                    *ngIf="badgedThreadCounterparty as counterparty"
                    [variant]="counterpartyTagVariant(counterparty)"
                    [tone]="counterparty"
                    [label]="counterpartyLabelKey(counterparty) | translate"
                    [matTooltip]="(isToVerify(activeConversationDetails?.counterparty_status)
                        ? ('smart_inbox.inbox_view.detail.counterparty_status_hint' | translate)
                        : ('smart_inbox.inbox_view.detail.thread_counterparty_hint' | translate))"
                    matTooltipClass="conversation-tooltip-info"
                    matTooltipPosition="below">
                </ui-ai-classification-tag>

                <!-- Thread categories — read-only. They are a rollup of the
                     messages' own categories, which are edited per message in
                     the timeline below. -->
                <div class="conversation-detail__categories" *ngIf="threadCategories.length">
                    <ui-ai-classification-tag *ngFor="let category of visibleThreadCategories"
                        [tone]="category"
                        [label]="classificationLabelKey(category) | translate"
                        [matTooltip]="'smart_inbox.inbox_view.detail.thread_categories_hint' | translate"
                        matTooltipClass="conversation-tooltip-info"
                        matTooltipPosition="below">
                    </ui-ai-classification-tag>

                    <!-- Overflow: the rest surface in a hover popover rather than
                         letting the row grow and push the other controls around. -->
                    <div class="conversation-detail__categories-more" *ngIf="hiddenThreadCategories.length">
                        <ui-ai-classification-tag
                            variant="muted"
                            [label]="'+' + hiddenThreadCategories.length">
                        </ui-ai-classification-tag>

                        <div class="conversation-detail__categories-popover" role="tooltip">
                            <ui-ai-classification-tag *ngFor="let category of hiddenThreadCategories"
                                [tone]="category"
                                [label]="classificationLabelKey(category) | translate">
                            </ui-ai-classification-tag>
                        </div>
                    </div>
                </div>

                <!-- Same divider as the one before "Assigned to", so labels are fenced
                     on both sides rather than running into the categories. -->
                <span class="conversation-detail__badges-separator" *ngIf="threadCategories.length"></span>

                <!-- Labels, beside the classification chips: same level of meaning,
                     and where the PO's design puts them. -->
                <!-- One chip definition, used on the row and inside the overflow
                     popover: two copies drifted apart the first time this changed. -->
                <ng-template #labelChip let-label>
                    <span class="conversation-detail__label-chip"
                          data-testid="label-chip"
                          [attr.data-label-id]="label.id">
                        <!-- Tag icon, tinted with the label colour: the design carries the
                             colour on the glyph, not as a separate dot or a coloured border. -->
                        <ui-svg-icon svgIcon="tag-cc" size="small"
                                     class="conversation-detail__label-icon"
                                     [style.color]="label.color"></ui-svg-icon>
                        <span class="conversation-detail__label-text" [title]="label.name">{{ label.name }}</span>
                        <!-- Every label detaches now. The removal is remembered, so the
                             sync does not put it back -- which is what the old refusal
                             was standing in for. -->
                        <button type="button"
                                class="conversation-detail__label-remove"
                                data-testid="label-chip-remove"
                                [attr.aria-label]="'smart_inbox.inbox_view.sidebar.remove_label' | translate"
                                (click)="onRemoveLabel(label.id)">
                            <ui-svg-icon svgIcon="close-cc" size="small"></ui-svg-icon>
                        </button>
                    </span>
                </ng-template>

                <ng-container *ngFor="let label of visibleConversationLabels; trackBy: trackByLabelId"
                              [ngTemplateOutlet]="labelChip"
                              [ngTemplateOutletContext]="{ $implicit: label }"></ng-container>

                <!-- Overflow, exactly as the categories block does it: the row must not
                     grow into "Assigned to" when a thread carries many labels. -->
                <div class="conversation-detail__labels-more"
                     data-testid="label-chip-overflow"
                     *ngIf="hiddenConversationLabels.length">
                    <span class="conversation-detail__label-chip conversation-detail__label-chip--more">
                        +{{ hiddenConversationLabels.length }}
                    </span>

                    <div class="conversation-detail__labels-popover" role="tooltip">
                        <ng-container *ngFor="let label of hiddenConversationLabels; trackBy: trackByLabelId"
                                      [ngTemplateOutlet]="labelChip"
                                      [ngTemplateOutletContext]="{ $implicit: label }"></ng-container>
                    </div>
                </div>

                <!-- Labels the provider already put on this thread that Volta has not
                     imported. Dashed and muted on purpose: they are not labels yet, and
                     a chip that looked real would promise filtering that does not work. -->
                <ng-template #pendingChip let-pending>
                <span class="conversation-detail__label-chip conversation-detail__label-chip--pending"
                      data-testid="pending-label-chip"
                      [attr.data-ref-id]="pending.ref_id"
                      [class.conversation-detail__label-chip--deciding]="decidingProviderRefId === pending.ref_id"
                      [title]="'smart_inbox.inbox_view.pending_labels.tooltip' | translate: { mailbox: pending.mailbox_email }">
                    <ui-svg-icon svgIcon="tag-cc" size="small"
                                 class="conversation-detail__label-icon"></ui-svg-icon>
                    <span class="conversation-detail__label-text">{{ pending.name }}</span>

                    <!-- The decision, on the chip itself. Two icons rather than two
                         words: the chip already carries a name that can be long, and
                         check/x are the pair everyone reads. The wording survives as
                         the tooltip and the accessible name. -->
                    <button type="button"
                            class="conversation-detail__label-decide"
                            data-testid="pending-label-import"
                            [disabled]="decidingProviderRefId !== null"
                            [title]="'smart_inbox.inbox_view.pending_labels.import' | translate"
                            [attr.aria-label]="'smart_inbox.inbox_view.pending_labels.import' | translate"
                            (click)="decidePendingProviderLabel(pending, 'accept')">
                        <ui-icon name="Check" size="small"></ui-icon>
                    </button>
                    <button type="button"
                            class="conversation-detail__label-decide conversation-detail__label-decide--muted"
                            data-testid="pending-label-ignore"
                            [disabled]="decidingProviderRefId !== null"
                            [title]="'smart_inbox.inbox_view.pending_labels.ignore' | translate"
                            [attr.aria-label]="'smart_inbox.inbox_view.pending_labels.ignore' | translate"
                            (click)="decidePendingProviderLabel(pending, 'reject')">
                        <ui-icon name="X" size="small"></ui-icon>
                    </button>
                </span>
                </ng-template>

                <ng-container *ngFor="let pending of visiblePendingProviderLabels; trackBy: trackByProviderRefId"
                              [ngTemplateOutlet]="pendingChip"
                              [ngTemplateOutletContext]="{ $implicit: pending }"></ng-container>

                <!-- Capped like the imported chips: several pending labels pushed
                     "+ Label" and "Assigned to" into the view selector. -->
                <div class="conversation-detail__labels-more"
                     data-testid="pending-label-overflow"
                     *ngIf="hiddenPendingProviderLabels.length">
                    <span class="conversation-detail__label-chip conversation-detail__label-chip--more conversation-detail__label-chip--pending-more">
                        +{{ hiddenPendingProviderLabels.length }}
                    </span>
                    <div class="conversation-detail__labels-popover" role="tooltip">
                        <ng-container *ngFor="let pending of hiddenPendingProviderLabels; trackBy: trackByProviderRefId"
                                      [ngTemplateOutlet]="pendingChip"
                                      [ngTemplateOutletContext]="{ $implicit: pending }"></ng-container>
                    </div>
                </div>

                <button type="button"
                        class="conversation-detail__label-add"
                        data-testid="label-picker-trigger"
                        [matMenuTriggerFor]="headerLabelMenu"
                        (click)="labelMenuIsOpen = true">
                    <ui-svg-icon svgIcon="tag-cc" size="small"></ui-svg-icon>
                    <span>{{ 'smart_inbox.inbox_view.label_picker.trigger' | translate }}</span>
                </button>

                <mat-menu #headerLabelMenu="matMenu"
                          class="inbox-menu inbox-view-menu"
                          (closed)="onLabelMenuClosed()">
                    <app-label-picker-menu
                        *ngIf="labelMenuIsOpen"
                        [appliedLabels]="conversationLabels"
                        (labelApplied)="onApplyLabel($event)"
                        (labelRemoved)="onRemoveLabel($event)"
                        (labelCreated)="onCreateLabel($event)">
                    </app-label-picker-menu>
                </mat-menu>

                <span class="conversation-detail__badges-separator" *ngIf="threadCategories.length"></span>

                <span class="conversation-detail__assign-label">{{ 'smart_inbox.inbox_view.detail.assigned_to' | translate }}</span>

                <!-- Assignee badge -->
                <span class="conversation-tag assigned-to is-badge is-action conversation-detail__assignee"
                        (click)="setActiveConversationMenu(CONVERSATION_MENU_OPTIONS.ASSIGN, 'single')"
                        [matMenuTriggerFor]="conversationActionMenuRef.menu">
                    <ng-container *ngIf="updatingAssignee" [ngTemplateOutlet]="badgeLoader"></ng-container>
                    <ng-container *ngIf="!updatingAssignee">
                        <span [class]="activeConversation?.assignee || activeConversationDetails?.assignee ? 'user-avatar' : ''" [style.backgroundColor]="activeConversation?.assignee || activeConversationDetails?.assignee ? getUserColor((activeConversation?.assignee || activeConversationDetails?.assignee)!) : ''">
                            <ng-container *ngIf="activeConversation?.assignee || activeConversationDetails?.assignee as user">{{ getUserInitials(user) }}</ng-container>
                            <ng-container *ngIf="!activeConversation?.assignee && !activeConversationDetails?.assignee">
                                <ui-svg-icon svgIcon="user_icon" size="medium"></ui-svg-icon>
                            </ng-container>
                        </span>
                        <span>{{ activeConversation?.assignee || activeConversationDetails?.assignee ? getUserFullname((activeConversation?.assignee || activeConversationDetails?.assignee)!) : ('smart_inbox.inbox_view.detail.unassigned' | translate) }}</span>
                        <ui-svg-icon svgIcon="chevron-down" size="medium"></ui-svg-icon>
                    </ng-container>
                </span>
            </div>

            <!-- View preferences + sidebar toggle -->
            <div class="conversation-detail__badges-right">
                <span class="conversation-detail__view-label">{{ 'smart_inbox.inbox_view.detail.view_label' | translate }}</span>
                <button type="button" class="conversation-detail__view-trigger" [matMenuTriggerFor]="timelineViewMenu">
                    <span>{{ activeTimelineViewLabelKey | translate }}</span>
                    <ui-svg-icon svgIcon="chevron-down" size="medium"></ui-svg-icon>
                </button>
                <ui-button type="transparent" icon="toggel_sidebar" iconSize="medium"
                    class="conversation-detail__sidebar-toggle"
                    (click)="toggleSidebar()"
                    [matTooltip]="(sidebarOpen ? 'smart_inbox.inbox_view.detail.sidebar_close' : 'smart_inbox.inbox_view.detail.sidebar_open') | translate"
                    matTooltipClass="conversation-tooltip-info">
                </ui-button>
            </div>

            <!-- Badge loader template -->
            <ng-template #badgeLoader>
                <span class="badge-loader">
                    <ui-svg-icon svgIcon="refresh-cc" size="small" class="spin-animation"></ui-svg-icon>
                </span>
            </ng-template>
        </div>

        <!-- ─── Timeline "View" preferences menu ──────────────────────── -->
        <mat-menu #timelineViewMenu="matMenu" class="inbox-menu" panelClass="inbox-view-menu">
            <ng-container *ngFor="let option of TIMELINE_VIEW_OPTIONS">
                <hr class="separator" *ngIf="option.divider" />
                <button mat-menu-item (click)="setTimelineViewMode(option.mode)">
                    <span class="timeline-view-option">
                        <span class="timeline-view-option__label">{{ option.labelKey | translate }}</span>
                        <span class="timeline-view-option__description" *ngIf="option.descriptionKey">{{ option.descriptionKey | translate }}</span>
                    </span>
                    <ui-svg-icon *ngIf="timelineViewMode === option.mode" svgIcon="check-cc" size="medium" class="timeline-view-option__check"></ui-svg-icon>
                </button>
            </ng-container>
        </mat-menu>

        <!-- ─── Message category menu ─────────────────────────────────── -->
        <mat-menu #messageCategoryMenu="matMenu" class="inbox-menu" panelClass="message-classification-menu">
            <ng-template matMenuContent let-message="message">
                <button mat-menu-item *ngFor="let category of CLASSIFICATION_OPTIONS"
                        (click)="onMessageCategorySelected(message, category)">
                    <ui-ai-classification-tag size="small" [tone]="category" [label]="classificationLabelKey(category) | translate"></ui-ai-classification-tag>
                    <ui-svg-icon *ngIf="message.classification === category" svgIcon="check-cc" size="medium" class="inbox-menu__check"></ui-svg-icon>
                </button>
            </ng-template>
        </mat-menu>

        <!-- ─── Message intent menu ───────────────────────────────────── -->
        <!-- Options are scoped to the message's current category. -->
        <mat-menu #messageIntentMenu="matMenu" class="inbox-menu" panelClass="message-classification-menu">
            <ng-template matMenuContent let-message="message">
                <button mat-menu-item (click)="onMessageIntentSelected(message, null)">
                    <span class="conversation-detail__intent-option is-muted">{{ 'smart_inbox.inbox_view.detail.intent_none' | translate }}</span>
                    <ui-svg-icon *ngIf="!message.intent" svgIcon="check-cc" size="medium" class="inbox-menu__check"></ui-svg-icon>
                </button>
                <hr class="separator" *ngIf="getIntentOptions(message).length" />
                <button mat-menu-item *ngFor="let intent of getIntentOptions(message)"
                        (click)="onMessageIntentSelected(message, intent)">
                    <span class="conversation-detail__intent-option">{{ getIntentLabel(intent) }}</span>
                    <ui-svg-icon *ngIf="message.intent === intent" svgIcon="check-cc" size="medium" class="inbox-menu__check"></ui-svg-icon>
                </button>
            </ng-template>
        </mat-menu>

        <!-- ─── Message counterparty menu ─────────────────────────────── -->
        <mat-menu #messageCounterpartyMenu="matMenu" class="inbox-menu" panelClass="message-classification-menu">
            <ng-template matMenuContent let-message="message">
                <ng-container *ngFor="let counterparty of COUNTERPARTY_OPTIONS">
                    <!-- `customer` is sent with a company: this item opens the picker as a submenu. -->
                    <button mat-menu-item *ngIf="counterpartyPickNeedsCompany(message, counterparty); else directCounterpartyPick"
                            [matMenuTriggerFor]="confirmCustomerMenu"
                            (menuOpened)="onMessageCounterpartySelected(message, counterparty)">
                        <ng-container *ngTemplateOutlet="counterpartyOption; context: { message: message, counterparty: counterparty }"></ng-container>
                    </button>
                    <ng-template #directCounterpartyPick>
                        <button mat-menu-item (click)="onMessageCounterpartySelected(message, counterparty)">
                            <ng-container *ngTemplateOutlet="counterpartyOption; context: { message: message, counterparty: counterparty }"></ng-container>
                        </button>
                    </ng-template>
                </ng-container>
            </ng-template>
        </mat-menu>

        <ng-template #counterpartyOption let-message="message" let-counterparty="counterparty">
            <ui-ai-classification-tag
                size="small"
                [variant]="counterpartyTagVariant(counterparty)"
                [tone]="counterparty"
                [label]="counterpartyLabelKey(counterparty) | translate">
            </ui-ai-classification-tag>
            <ui-svg-icon *ngIf="messageCounterparty(message) === counterparty" svgIcon="check-cc" size="medium" class="inbox-menu__check"></ui-svg-icon>
        </ng-template>

        <!-- ─── Body (timeline + sidebar) ────────────────────────────── -->
        <div class="conversation-detail__body" [class.sidebar-open]="sidebarOpen" *ngIf="activeConversationDetails && !conversationDetailsLoading">

            <!-- ─── Timeline (messages + events) ───────────────────────── -->
            <div class="conversation-detail__content">
                <div class="conversation-detail__timeline" #timelineContainer>
                    <!-- Timeline: objects (messages + events) -->
                    <ng-container *ngIf="timelineItems as items">
                        <ng-container *ngFor="let item of items; trackBy: trackByTimelineItem">
                            <!-- Timeline message: full message UI -->
                            <div class="conversation-detail__timeline-message" #messageItem
                                 [attr.data-message-id]="item.id"
                                 [class.is-collapsed]="!isMessageExpanded(item.id)"
                                 *ngIf="isTimelineMessage(item); else timelineOrderOrOther">

                                <div class="conversation-detail__timeline-message-header" (click)="toggleMessage(item.id)" role="button" tabindex="0" (keydown.enter)="toggleMessage(item.id)" (keydown.space)="toggleMessage(item.id); $event.preventDefault()">
                                    <div class="conversation-detail__timeline-message-icon">
                                        <ui-svg-icon [svgIcon]="item.sender_email === activeConversationDetails.mailbox.email ? 'reply' : 'box'" size="medium"></ui-svg-icon>
                                    </div>
                                    <div class="conversation-detail__timeline-message-info" [class.conversation-detail__timeline-message-info-expanded]="isMessageExpanded(item.id)" [class.conversation-detail__timeline-message-info-collapsed]="!isMessageExpanded(item.id)">
                                        <ng-container *ngIf="!isMessageExpanded(item.id)">
                                            <span class="conversation-detail__timeline-message-sender">{{ getDisplaySenderName(item) }}<span *ngIf="item.sent_by_automation" class="conversation-detail__timeline-message-automation" [matTooltip]="item.automation_name || ('smart_inbox.timeline.automation_user' | translate)" matTooltipClass="conversation-tooltip-info"><ui-svg-icon svgIcon="bolt" size="xsmall"></ui-svg-icon>{{ 'smart_inbox.timeline.sent_by_automation' | translate }}</span></span>
                                            <div class="conversation-detail__timeline-message-preview">{{ getMessagePreview(item.body) }}</div>
                                        </ng-container>
                                        <ng-container *ngIf="isMessageExpanded(item.id)">
                                            <span class="conversation-detail__timeline-message-sender">
                                                {{ getDisplaySenderName(item) }}
                                                <span *ngIf="item.sent_by_automation" class="conversation-detail__timeline-message-automation" [matTooltip]="item.automation_name || ('smart_inbox.timeline.automation_user' | translate)" matTooltipClass="conversation-tooltip-info"><ui-svg-icon svgIcon="bolt" size="xsmall"></ui-svg-icon>{{ 'smart_inbox.timeline.sent_by_automation' | translate }}</span>
                                            </span>
                                            <span class="conversation-detail__timeline-message-recipients">
                                                {{ 'smart_inbox.inbox_view.detail.to_label' | translate }} {{ getDisplayRecipients(item.recipients) }}
                                                <ui-svg-icon svgIcon="arrow_down" size="xsmall"
                                                    class="message-details-toggle"
                                                    [matMenuTriggerFor]="messageDetailsMenu"
                                                    (click)="$event.stopPropagation()">
                                                </ui-svg-icon>
                                                <mat-menu #messageDetailsMenu="matMenu" class="message-details-menu">
                                                    <div class="message-details-menu__content" (click)="$event.stopPropagation()">
                                                        <div class="message-details-menu__row">
                                                            <span class="message-details-menu__label">{{ 'smart_inbox.inbox_view.detail.from_label' | translate }}</span>
                                                            <span class="message-details-menu__value">{{ item.sender_name }} &lt;{{ item.sender_email }}&gt;</span>
                                                        </div>
                                                        <div class="message-details-menu__row">
                                                            <span class="message-details-menu__label">{{ 'smart_inbox.inbox_view.detail.to_label' | translate }}</span>
                                                            <span class="message-details-menu__value">{{ item.recipients }}</span>
                                                        </div>
                                                        <div *ngIf="item.cc" class="message-details-menu__row">
                                                            <span class="message-details-menu__label">{{ 'smart_inbox.inbox_view.detail.cc_label' | translate }}</span>
                                                            <span class="message-details-menu__value">{{ item.cc }}</span>
                                                        </div>
                                                        <div *ngIf="item.bcc && item.bcc !== true" class="message-details-menu__row">
                                                            <span class="message-details-menu__label">{{ 'smart_inbox.inbox_view.detail.bcc_label' | translate }}</span>
                                                            <span class="message-details-menu__value">{{ item.bcc }}</span>
                                                        </div>
                                                        <div class="message-details-menu__row">
                                                            <span class="message-details-menu__label">{{ 'smart_inbox.inbox_view.detail.date_label' | translate }}</span>
                                                            <span class="message-details-menu__value">{{ item.timestamp | adaptToUserUtcOffset | date:'d MMM y, HH:mm' }}</span>
                                                        </div>
                                                        <div class="message-details-menu__row">
                                                            <span class="message-details-menu__label">{{ 'smart_inbox.inbox_view.detail.subject_label' | translate }}</span>
                                                            <span class="message-details-menu__value">{{ item.subject }}</span>
                                                        </div>
                                                    </div>
                                                </mat-menu>
                                            </span>
                                        </ng-container>
                                    </div>
                                    <div class="conversation-detail__timeline-message-actions">
                                        <!--
                                            The date is always the LAST element in flow so it lands on the
                                            card's right padding edge, in line with every other timeline
                                            item. The expand chevron sits before it, and the reply actions
                                            are taken out of flow (see the SCSS) so they cannot shift it.
                                        -->
                                        <ng-container *ngIf="!isMessageExpanded(item.id)">
                                            <ui-svg-icon *ngIf="(item.attachment_count ?? 0) > 0" svgIcon="thread-attachment" size="small" [matTooltip]="'smart_inbox.inbox_view.detail.attachments_count' | translate:{ count: item.attachment_count ?? 0 }" matTooltipClass="conversation-tooltip-info" matTooltipPosition="right"></ui-svg-icon>
                                            <ui-svg-icon svgIcon="chevron_right" size="small"></ui-svg-icon>
                                            <div class="conversation-detail__timeline-message-date">{{ item.timestamp | daysAgoOrLeft: 'ago' : 2 : 'DD/MM/YYYY' }}</div>
                                        </ng-container>
                                        <ng-container *ngIf="isMessageExpanded(item.id)">
                                            <div class="conversation-detail__timeline-message-date">{{ item.timestamp | daysAgoOrLeft: 'ago' : 2 : 'DD/MM/YYYY' }}</div>
                                            <div class="conversation-detail__timeline-message-reply-actions">
                                                <ui-button *ngIf="isInboundMessage(item)" type="transparent" icon="ai-volta-icon" iconSize="medium"
                                                    [matTooltip]="'smart_inbox.inbox_view.detail.ai_reply' | translate" matTooltipClass="conversation-tooltip-info"
                                                    (click)="quickAiReply($event, item)"></ui-button>
                                                <ui-button type="transparent" icon="reply" iconSize="medium"
                                                    [matTooltip]="'smart_inbox.inbox_view.detail.reply' | translate" matTooltipClass="conversation-tooltip-info"
                                                    (click)="replyToMessage($event, 'reply', item)"></ui-button>
                                                <ui-button *ngIf="hasReplyAllRecipients(item)" type="transparent" icon="reply-all" iconSize="medium"
                                                    [matTooltip]="'smart_inbox.inbox_view.detail.reply_all' | translate" matTooltipClass="conversation-tooltip-info"
                                                    (click)="replyToMessage($event, 'reply-all', item)"></ui-button>
                                                <ui-button type="transparent" icon="forward" iconSize="medium"
                                                    [matTooltip]="'smart_inbox.inbox_view.detail.forward' | translate" matTooltipClass="conversation-tooltip-info"
                                                    (click)="forwardMessage($event, item)"></ui-button>
                                            </div>
                                        </ng-container>
                                    </div>
                                </div>

                                <!-- AI classification of this message: category > intent >
                                     counterparty, all overridable. Mail with no verdict shows nothing. -->
                                <div class="conversation-detail__message-classification"
                                     *ngIf="isMessageExpanded(item.id)
                                            && (isInboundMessage(item) || isClassifiedMessage(item))">
                                    <ui-ai-classification-tag
                                        size="small"
                                        [editable]="true"
                                        [tone]="item.classification || 'undefined'"
                                        [label]="classificationLabelKey(item.classification) | translate"
                                        [loading]="isClassifying(item.id)"
                                        [tooltip]="item.reasoning || ('smart_inbox.inbox_view.detail.tooltip_change_category' | translate)"
                                        [matMenuTriggerFor]="messageCategoryMenu"
                                        [matMenuTriggerData]="{ message: item }">
                                    </ui-ai-classification-tag>

                                    <!-- An uncategorised message has no meaningful intent to
                                         pick, so the picker and its separator are hidden
                                         until a category is set. -->
                                    <ng-container *ngIf="hasIntentPicker(item)">
                                        <ui-svg-icon class="conversation-detail__message-classification-sep" svgIcon="chevron_right" size="xsmall"></ui-svg-icon>

                                        <ui-ai-classification-tag
                                            size="small"
                                            [editable]="true"
                                            [variant]="item.intent ? 'muted' : 'dashed'"
                                            [label]="getIntentLabel(item.intent)"
                                            [loading]="isClassifying(item.id)"
                                            [tooltip]="'smart_inbox.inbox_view.detail.tooltip_change_intent' | translate"
                                            [matMenuTriggerFor]="messageIntentMenu"
                                            [matMenuTriggerData]="{ message: item }">
                                        </ui-ai-classification-tag>
                                    </ng-container>

                                    <!-- Counterparty: always offered, even on an uncategorised message. -->
                                    <ui-ai-classification-tag
                                        *ngIf="messageCounterparty(item) as counterparty"
                                        class="conversation-detail__message-counterparty"
                                        size="small"
                                        [editable]="true"
                                        [variant]="counterpartyTagVariant(counterparty)"
                                        [tone]="counterparty"
                                        [label]="counterpartyLabelKey(counterparty) | translate"
                                        [loading]="isClassifying(item.id)"
                                        [tooltip]="'smart_inbox.inbox_view.detail.tooltip_change_counterparty' | translate"
                                        [matMenuTriggerFor]="messageCounterpartyMenu"
                                        [matMenuTriggerData]="{ message: item }">
                                    </ui-ai-classification-tag>

                                    <!-- Unconfirmed verdict: confidence and source. -->
                                    <!-- Two buttons, one chip: `matMenuTriggerFor` does not accept a null panel,
                                         so only the customer case carries the trigger. -->
                                    <button *ngIf="canConfirmCounterparty(item) && confirmNeedsCompany(messageCounterparty(item))"
                                            type="button"
                                            class="conversation-detail__message-to-verify conversation-detail__message-to-verify--confirmable"
                                            [matMenuTriggerFor]="confirmCustomerMenu"
                                            (click)="confirmMessageCounterparty(item)"
                                            [attr.aria-label]="'smart_inbox.inbox_view.detail.counterparty_confirm_pick_company' | translate"
                                            [matTooltip]="'smart_inbox.inbox_view.detail.counterparty_confirm_pick_company' | translate"
                                            matTooltipClass="conversation-tooltip-info"
                                            matTooltipPosition="below">
                                        <ng-container *ngTemplateOutlet="toVerifyChip; context: { $implicit: item }"></ng-container>
                                    </button>

                                    <button *ngIf="canConfirmCounterparty(item) && !confirmNeedsCompany(messageCounterparty(item))"
                                            type="button"
                                            class="conversation-detail__message-to-verify conversation-detail__message-to-verify--confirmable"
                                            (click)="confirmMessageCounterparty(item)"
                                            [attr.aria-label]="'smart_inbox.inbox_view.detail.counterparty_confirm' | translate"
                                            [matTooltip]="counterpartyTooltip(item, 'smart_inbox.inbox_view.detail.counterparty_confirm_hint')"
                                            matTooltipClass="conversation-tooltip-info"
                                            matTooltipPosition="below">
                                        <ng-container *ngTemplateOutlet="toVerifyChip; context: { $implicit: item }"></ng-container>
                                    </button>

                                    <!-- Dual role: settled, nothing to confirm. -->
                                    <span *ngIf="isDualRoleMessage(item)"
                                          class="conversation-detail__message-to-verify conversation-detail__message-dual-role"
                                          [matTooltip]="counterpartyDualRoleTooltip(item)"
                                          matTooltipClass="conversation-tooltip-info"
                                          matTooltipPosition="below">
                                        {{ 'smart_inbox.inbox_view.detail.counterparty_dual_role_chip' | translate }}
                                    </span>

                                    <!-- `unknown` has nothing to confirm; the tag menu answers it. -->
                                    <span *ngIf="!isDualRoleMessage(item) && isToVerify(item.counterparty_status) && !canConfirmCounterparty(item)"
                                          class="conversation-detail__message-to-verify"
                                          [matTooltip]="counterpartyTooltip(item)"
                                          matTooltipClass="conversation-tooltip-info"
                                          matTooltipPosition="below">
                                        <ng-container *ngTemplateOutlet="toVerifyChip; context: { $implicit: item }"></ng-container>
                                    </span>

                                    <ui-svg-icon *ngIf="item.is_urgent"
                                                 class="conversation-detail__message-urgent"
                                                 svgIcon="flag-filled"
                                                 size="small"
                                                 [matTooltip]="'smart_inbox.inbox_view.detail.urgency_message_urgent' | translate"
                                                 matTooltipClass="conversation-tooltip-info">
                                    </ui-svg-icon>
                                </div>

                                <!-- The AI disagrees with the verdict on this message; the badge does not change. -->
                                <div *ngIf="isMessageExpanded(item.id) && showsCounterpartyConflict(item)"
                                     class="conversation-detail__counterparty-conflict">
                                    <ui-svg-icon svgIcon="info" size="small"></ui-svg-icon>
                                    <span class="conversation-detail__counterparty-conflict-text">
                                        <ng-container *ngIf="counterpartyModelReading(item.counterparty_model, item.counterparty_model_confidence) as reading; else conflictWithoutReading">
                                            {{ 'smart_inbox.inbox_view.detail.counterparty_conflict_question' | translate:{
                                                model: (counterpartyLabelKey(reading.value) | translate),
                                                percent: reading.percent
                                            } }}
                                        </ng-container>
                                        <!-- Fallback when the message has no stored AI reading. -->
                                        <ng-template #conflictWithoutReading>
                                            {{ 'smart_inbox.inbox_view.detail.counterparty_conflict_question_generic' | translate }}
                                        </ng-template>
                                    </span>
                                    <button type="button"
                                            class="conversation-detail__counterparty-conflict-accept"
                                            (click)="acceptDualRole(item)">
                                        {{ 'smart_inbox.inbox_view.detail.counterparty_dual_role_accept' | translate }}
                                    </button>
                                </div>

                                <ng-container *ngIf="isMessageExpanded(item.id)">
                                    <div class="conversation-detail__timeline-message-content">
                                        <div class="conversation-detail__timeline-message-body" [innerHTML]="getReplyHtml(item.body)"></div>
                                        <div class="conversation-detail__quoted-content" *ngIf="isQuoteExpanded(item.id) && hasQuotedContent(item.body)">
                                            <div class="conversation-detail__timeline-message-body" [innerHTML]="getQuotedHtml(item.body)"></div>
                                        </div>
                                    </div>
                                    <div class="conversation-detail__quote-toggle" *ngIf="hasQuotedContent(item.body)" (click)="toggleQuote(item.id, $event)">
                                        <span class="quote-toggle-dots"><ui-svg-icon svgIcon="more_horiz" size="medium"></ui-svg-icon></span>
                                    </div>
                                    <hr class="separator" *ngIf="item.has_attachments"/>
                                    <div class="conversation-detail__attachments" *ngIf="item.has_attachments">
                                        <div class="attachment" *ngFor="let attachment of (item.attachments ?? [])">
                                            <ui-svg-icon svgIcon="thread-attachment" size="small"></ui-svg-icon>
                                            <span>{{ attachment.name }}</span>
                                            <span class="attachment-size">{{ attachment.size | fileSize }}</span>
                                            <span class="preview" *ngIf="isPreviewable(attachment)">
                                                <ui-svg-icon svgIcon="eye" size="medium" [matTooltip]="'smart_inbox.inbox_view.detail.tooltip_preview' | translate" matTooltipClass="conversation-tooltip-info" matTooltipPosition="right" (click)="previewAttachment(attachment)"></ui-svg-icon>
                                            </span>
                                            <span class="download">
                                                <ui-svg-icon svgIcon="download" size="medium" [matTooltip]="attachment.name" matTooltipClass="conversation-tooltip-info" matTooltipPosition="right" (click)="downloadAttachment(attachment)"></ui-svg-icon>
                                            </span>
                                        </div>
                                    </div>
                                </ng-container>
                            </div>

                            <!-- Timeline order -->
                            <ng-template #timelineOrderOrOther>
                                <!-- Order artifact, in-progress state (WB-7401) -->
                                <app-order-artifact
                                    *ngIf="isTimelineOrderPlaceholder(item)"
                                    state="creating">
                                </app-order-artifact>

                                <!-- Order artifact, created state (WB-7401) -->
                                <app-order-artifact
                                    *ngIf="isTimelineOrder(item)"
                                    state="created"
                                    [order]="item"
                                    (openOrder)="openOrderCorrectionPanel($event)"
                                    (viewFullOrder)="openOrderInNewTab($event)"
                                    (downloadPdf)="downloadQuotationPdf($event)"
                                    (orderChanged)="reloadActiveConversationDetails()">
                                </app-order-artifact>

                                <!-- The N orders one create-order run produced (WB-7598) -->
                                <app-order-group-artifact
                                    *ngIf="isTimelineOrderGroup(item)"
                                    [group]="item"
                                    (openOrder)="openOrderCorrectionPanel($event)"
                                    (orderChanged)="reloadActiveConversationDetails()">
                                </app-order-group-artifact>

                                <!-- Timeline event or comment -->
                                <ng-container *ngIf="!isTimelineOrder(item) && !isTimelineOrderPlaceholder(item) && !isTimelineOrderGroup(item)">
                                    <!-- Timeline event -->
                                    <div class="conversation-detail__timeline-event" *ngIf="isTimelineEvent(item)">
                                        <span class="conversation-detail__timeline-event-icon">
                                            <ui-svg-icon 
                                            [svgIcon]="getHumanReadableTimelineEvent(item).icon" 
                                            size="small"  
                                            [matTooltip]="getAutomationTooltip(item)"
                                            matTooltipClass="conversation-tooltip-info">
                                            </ui-svg-icon>
                                        </span>
                                        <span class="conversation-detail__timeline-event-message" [innerHTML]="getHumanReadableTimelineEvent(item).message"></span>
                                        <a *ngIf="item.event_type === 'reply_drafted' && item.draft_id"
                                           class="conversation-detail__timeline-event-link"
                                           (click)="openDraftFromEvent(item)">{{ 'smart_inbox.timeline.open_draft' | translate }}</a>
                                        <span class="conversation-detail__timeline-event-date">{{ item.created_at | daysAgoOrLeft: 'ago' : 2 : 'DD/MM/YYYY HH:mm' }}</span>
                                    </div>

                                    <!-- Timeline comment -->
                                    <div class="conversation-detail__timeline-comment" *ngIf="isTimelineComment(item)"
                                         [attr.data-comment-id]="item.id"
                                         >
                                        <span
                                            class="conversation-detail__timeline-comment-avatar"
                                            [style.backgroundColor]="item.source === 'automation' ? null : getUserColor(item.author)">
                                            <ui-svg-icon *ngIf="item.source === 'automation'; else commentAuthorInitials" svgIcon="bolt" size="small" [matTooltip]="getAutomationTooltip(item)"
                                            matTooltipClass="conversation-tooltip-info"></ui-svg-icon>
                                            <ng-template #commentAuthorInitials>
                                                {{ getUserInitials({ name: item.author.name }) }}
                                            </ng-template>
                                        </span>
                                        <div class="conversation-detail__timeline-comment-content">
                                            <div class="conversation-detail__timeline-comment-header">
                                                <span class="conversation-detail__timeline-comment-sender">{{ getTimelineActorName(item) }}</span>
                                                <span class="conversation-detail__timeline-comment-date">{{ item.updated_at | daysAgoOrLeft: 'ago' : 2 : 'DD/MM/YYYY HH:mm' }}</span>
                                            </div>
                                            <div class="conversation-detail__comment-edit" *ngIf="editingCommentId === item.id">
                                                <textarea #commentEditInput
                                                    class="conversation-detail__comment-edit-input"
                                                    [placeholder]="'smart_inbox.inbox_view.detail.comment_edit_placeholder' | translate"
                                                    [(ngModel)]="editedCommentBody"
                                                    [mention]="getMentionables()"
                                                    [mentionConfig]="commentMentionConfig"
                                                    [mentionListTemplate]="commentMentionTemplate"
                                                    (searchTerm)="onMentionSearch($event)"
                                                    (itemSelected)="onMentionSelected($event, true)"
                                                    (keydown)="onCommentKeydown($event, 'edit')"
                                                    (input)="growCommentInput('edit')"></textarea>
                                                <div class="conversation-detail__comment-edit-actions">
                                                    <ui-button
                                                        type="transparent"
                                                        icon="check-cc"
                                                        iconSize="medium"
                                                        [isLoading]="loadingCommentId === item.id"
                                                        [disabled]="loadingCommentId === item.id || !editedCommentBody.trim()"
                                                        (click)="confirmEditComment()">
                                                    </ui-button>
                                                    <ui-button
                                                        type="transparent"
                                                        icon="close"
                                                        iconSize="medium"
                                                        [disabled]="loadingCommentId === item.id"
                                                        (click)="cancelEditComment()">
                                                    </ui-button>
                                                </div>
                                            </div>
                                            <div class="conversation-detail__timeline-comment-body"
                                                *ngIf="editingCommentId !== item.id"
                                                [innerHTML]="getCommentBodyHtml(item.body, item.mentions)"
                                                (click)="onMentionClick($event)"></div>
                                        </div>
                                        <!-- Same `ui-button type="transparent"` affordance as the message
                                             card's reply actions, and taken out of flow (see the SCSS) so
                                             they cannot push the timestamp off the right edge. -->
                                        <div class="conversation-detail__timeline-comment-actions" *ngIf="editingCommentId !== item.id">
                                            <ui-button type="transparent" icon="copy" iconSize="medium"
                                                [matTooltip]="'smart_inbox.inbox_view.detail.tooltip_copy' | translate" matTooltipClass="conversation-tooltip-info"
                                                (click)="copyComment(item)"></ui-button>
                                            <ui-button type="transparent" icon="pen-cc" iconSize="medium"
                                                *ngIf="canEditComment(item) && item.source !== 'automation'"
                                                [matTooltip]="'smart_inbox.inbox_view.detail.tooltip_edit' | translate" matTooltipClass="conversation-tooltip-info"
                                                (click)="startEditingComment(item)"></ui-button>
                                            <ui-button type="transparent" icon="trash-cc" iconSize="medium"
                                                class="conversation-detail__timeline-comment-action--delete"
                                                *ngIf="canEditComment(item) && item.source !== 'automation'"
                                                [matTooltip]="'smart_inbox.inbox_view.detail.tooltip_delete' | translate" matTooltipClass="conversation-tooltip-info"
                                                (click)="onCommentDelete(item)"></ui-button>
                                        </div>
                                    </div>
                                </ng-container>
                            </ng-template>
                        </ng-container>
                    </ng-container>
                </div>

                <!-- ─── Action bar (create order · AI draft · internal message) ─── -->
                <div class="conversation-detail__actions-bar" *ngIf="selectedConversationIds.length === 0">
                    <div class="conversation-detail__actions-row">
                        <ui-button
                            type="primary"
                            icon="cart"
                            iconSize="medium"
                            [label]="'smart_inbox.inbox_view.detail.create_order' | translate"
                            [isLoading]="creatingOrder"
                            testId="create-order-btn"
                            (click)="createOrder()">
                        </ui-button>
                        <ui-button
                            type="outlined"
                            icon="ai-volta-icon"
                            iconSize="medium"
                            class="conversation-detail__action-icon"
                            [matTooltip]="'smart_inbox.inbox_view.detail.ai_draft' | translate"
                            matTooltipClass="conversation-tooltip-info"
                            (click)="aiDraftForThread($event)">
                        </ui-button>
                        <ui-button
                            type="outlined"
                            icon="comment"
                            iconSize="medium"
                            class="conversation-detail__action-icon"
                            [class.is-active]="internalComposerOpen"
                            [matTooltip]="'smart_inbox.inbox_view.detail.internal_message' | translate"
                            matTooltipClass="conversation-tooltip-info"
                            (click)="toggleInternalComposer()">
                        </ui-button>
                        <ui-button *ngIf="internalComposerOpen"
                            type="transparent"
                            icon="close"
                            iconSize="medium"
                            class="conversation-detail__actions-close"
                            [matTooltip]="'smart_inbox.inbox_view.detail.internal_message_close' | translate"
                            matTooltipClass="conversation-tooltip-info"
                            (click)="closeInternalComposer()">
                        </ui-button>
                    </div>

                    <!-- Internal message composer (toggled by the internal-message action) -->
                    <div class="conversation-detail__comment-form" *ngIf="internalComposerOpen">
                        <textarea #commentInput class="conversation-detail__comment-input" [placeholder]="'smart_inbox.inbox_view.detail.comment_placeholder' | translate"
                    [(ngModel)]="commentInputValue"
                    [mention]="getMentionables()"
                    [mentionConfig]="commentMentionConfig"
                    [mentionListTemplate]="commentMentionTemplate"
                    (searchTerm)="onMentionSearch($event)"
                    (itemSelected)="onMentionSelected($event, false)"
                    (opened)="mentionListOpen = true"
                    (closed)="mentionListOpen = false"
                    (keydown)="onCommentKeydown($event, 'comment')"
                    (input)="growCommentInput('comment')"></textarea>
                        <ui-button type="transparent" icon="send-cc" [disabled]="commentInputValue.trim().length === 0" (click)="createComment()"></ui-button>
                    </div>
                </div>
                <ng-template #commentMentionTemplate let-item="item">
                    <span class="user-avatar" [style.backgroundColor]="getUserColor(item)">
                        {{ item.initials }}
                    </span>
                    <span>{{ item.name }}</span>
                </ng-template>           
            </div>

            <!-- ─── Sidebar ──────────────────────────────────────────── -->
            <app-conversation-detail-sidebar></app-conversation-detail-sidebar>

            
        </div>

        <!-- ─── Messages Loading State ────────────────────────────────── -->
        <ng-container *ngIf="conversationDetailsLoading">
            <div class="conversation-detail__empty pulse-bg">
                <ui-svg-icon svgIcon="refresh-cc" size="xlarge" [class.spin-animation]="conversationDetailsLoading"></ui-svg-icon>
            </div>
        </ng-container>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- Bulk Actions Bar (shown when any conversations selected)           -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- The four actions a user comes to this bar for carry labels. Only
         read/unread stays icon-only, since its meaning flips with the selection
         and its tooltip says which way it will go. -->
    <ui-bulk-actions-bar
        *ngIf="selectedConversationIds.length > 0"
        [count]="selectedConversationIds.length"
        (cleared)="onSelectionCleared()">

        <button class="ui-bulk-action"
                [matMenuTriggerFor]="conversationActionMenuRef.menu"
                (click)="setActiveConversationMenu(CONVERSATION_MENU_OPTIONS.CATEGORY, 'bulk')">
            <ui-icon name="Tag" size="medium"></ui-icon>
            <span>{{ 'smart_inbox.inbox_view.detail.bulk.categorize' | translate }}</span>
        </button>

        <button class="ui-bulk-action"
                [matMenuTriggerFor]="conversationActionMenuRef.menu"
                (click)="setActiveConversationMenu(CONVERSATION_MENU_OPTIONS.ASSIGN, 'bulk')">
            <ui-icon name="UserPlus" size="medium"></ui-icon>
            <span>{{ 'smart_inbox.inbox_view.detail.bulk.assign_to' | translate }}</span>
        </button>

        <button class="ui-bulk-action"
                #customerAssociationMenuTriggerBulk="matMenuTrigger"
                [matMenuTriggerFor]="customerAssociationMenu"
                (click)="onOpenCustomerMenu(); conversationUpdateType = 'bulk'">
            <ui-icon name="Store" size="medium"></ui-icon>
            <span>{{ 'smart_inbox.inbox_view.detail.bulk.company' | translate }}</span>
        </button>

        <button class="ui-bulk-action"
                [matMenuTriggerFor]="conversationActionMenuRef.menu"
                (click)="setActiveConversationMenu(CONVERSATION_MENU_OPTIONS.STATUS, 'bulk')">
            <ui-icon name="Circle" size="medium"></ui-icon>
            <span>{{ 'smart_inbox.inbox_view.detail.bulk.change_status' | translate }}</span>
        </button>

        <button class="ui-bulk-action ui-bulk-action--icon"
                [matTooltip]="(bulkMarkAsRead ? 'smart_inbox.inbox_view.detail.tooltip_mark_read' : 'smart_inbox.inbox_view.detail.tooltip_mark_unread') | translate"
                matTooltipPosition="above"
                [attr.aria-label]="(bulkMarkAsRead ? 'smart_inbox.inbox_view.detail.tooltip_mark_read' : 'smart_inbox.inbox_view.detail.tooltip_mark_unread') | translate"
                (click)="conversationUpdateType = 'bulk'; onMarkRead()">
            <ui-icon [name]="bulkMarkAsRead ? 'MailOpen' : 'Mail'" size="medium"></ui-icon>
        </button>
    </ui-bulk-actions-bar>
</div>

<!-- "To verify" chip content: status at rest, confirm label on hover (CSS). -->
<ng-template #toVerifyChip let-item>
  <span class="conversation-detail__message-to-verify-idle">
    {{ 'smart_inbox.inbox_view.detail.counterparty_status_to_verify' | translate }}
    <!-- Not `as percent`: it would hide a 0. -->
    <ng-container *ngIf="counterpartyConfidencePercent(item.counterparty_confidence, item.counterparty_status) !== null">
      · {{ counterpartyConfidencePercent(item.counterparty_confidence, item.counterparty_status) }}&nbsp;%
    </ng-container>
  </span>
  <span class="conversation-detail__message-to-verify-confirm">
    <ui-svg-icon svgIcon="check-cc" size="small"></ui-svg-icon>
    {{ 'smart_inbox.inbox_view.detail.counterparty_confirm' | translate }}
  </span>
</ng-template>

<!-- ─── Confirming a `customer` verdict: which company? ──────────────────── -->
<mat-menu #confirmCustomerMenu="matMenu" class="inbox-menu inbox-view-menu company-picker-menu"
  (closed)="cancelCustomerConfirmation()">
  <app-customer-association-menu
    *ngIf="pendingCustomerConfirmation"
    [selectedCustomerId]="activeConversationDetails?.customer?.id ?? null"
    [showRemove]="false"
    updateType="single"
    (customerSelected)="onConfirmCustomerCompany($event)">
  </app-customer-association-menu>
</mat-menu>

<!-- ─── Customer (client) association menu ─────────────────────────────── -->
<mat-menu #customerAssociationMenu="matMenu" class="inbox-menu inbox-view-menu company-picker-menu"
  (closed)="customerMenuIsOpen = false">
  <app-customer-association-menu
    *ngIf="customerMenuIsOpen"
    #customerAssociationMenuRef
    [selectedCustomerId]="activeConversation?.customer?.id ?? null"
    [updateType]="conversationUpdateType"
    (customerSelected)="onCustomerSelected($event); closeCustomerMenu();"
    (associationRemoved)="onConversationUpdated({ customer_id: null }); closeCustomerMenu();">
  </app-customer-association-menu>
</mat-menu>

<!-- ─── Embedded Thread Action Menu ────────────────────────────────────── -->
<app-conversation-action-menu
    [activeMenu]="activeConversationMenu"
    [updateType]="conversationUpdateType"
    [activeConversation]="activeConversation"
    (updated)="onConversationUpdated($event)"
    (markAsUnread)="onMarkRead()">
</app-conversation-action-menu>
